import React, { useEffect, useState } from "react";
import Layout from "../common/Layout";
import LoadingSpinner from "../common/LoadingSpinner";
import profileService from "../../services/profileService";
import { useAuth } from "../../contexts/AuthContext";
import NotificationPreferences from "./NotificationPreferences";
import ChangePassword from "./ChangePassword";
import ProfileImageUpload from "./ProfileImageUpload";
import { getTimeZoneOptions } from "../../utils/timezone";
import "../../styles/Profile.css";

function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [profile, setProfile] = useState(null);

  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [timezone, setTimezone] = useState("Asia/Kolkata");

  useEffect(() => {
    let cancelled = false;
    profileService
      .get()
      .then((data) => {
        if (cancelled || !data) return;
        setProfile(data);
        setFullName(data.fullName || "");
        setCompanyName(data.companyName || "");
        setTimezone(data.timezone || "Asia/Kolkata");
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error || "Failed to load profile");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await profileService.update({
        fullName,
        companyName,
        timezone,
      });
      setProfile((prev) => ({ ...prev, ...updated }));
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading profile..." />;

  return (
    <Layout title="My Profile" subtitle="Manage your account and preferences">
      <div className="profile-page">
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        )}
        {savedAt && !error && (
          <div className="profile-saved">
            <i className="fas fa-check-circle" /> Saved
          </div>
        )}

        <section className="profile-section">
          <h3>Profile</h3>
          <ProfileImageUpload
            currentUrl={profile?.profileImageUrl}
            initials={fullName}
            onUploaded={(url) =>
              setProfile((prev) => ({ ...prev, profileImageUrl: url }))
            }
          />
          <div className="form-group">
            <label>Email</label>
            <input className="form-control" value={profile?.email || ""} readOnly />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input
                className="form-control"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Company</label>
              <input
                className="form-control"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Role</label>
              <input className="form-control" value={profile?.role || ""} readOnly />
            </div>
            <div className="form-group">
              <label>Time Zone</label>
              <select
                className="form-control"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                {getTimeZoneOptions().map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save" /> Save Profile
                </>
              )}
            </button>
          </div>
        </section>

        <NotificationPreferences
          initial={profile?.notificationPrefs}
          onSaved={(prefs) =>
            setProfile((prev) => ({ ...prev, notificationPrefs: prefs }))
          }
        />

        <ChangePassword />
      </div>
    </Layout>
  );
}

export default ProfilePage;